// Phase A.3 / 8A.7 / 8C — SIKA chat composer (§6).
//
// Frosted-glass sticky bottom bar with textarea, attachment button,
// send button with states (empty/ready/loading), keyboard hint,
// and voice input via Web Speech API (Expansion Moat Phase 07).

import { useCallback, useRef, useState, type KeyboardEvent } from "react";
import { Icon } from "../../components/icons/Icon.tsx";

interface Props {
  onSend: (text: string) => void;
  disabled: boolean;
  onAttach?: () => void;
}

/** Check if Web Speech API is available */
const SPEECH_AVAILABLE =
  typeof window !== "undefined" &&
  ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

export function SikaChatInput({ onSend, disabled, onAttach }: Props): JSX.Element {
  const [value, setValue] = useState("");
  const [listening, setListening] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<unknown>(null);

  const hasText = value.trim().length > 0;

  const toggleVoice = useCallback(() => {
    if (!SPEECH_AVAILABLE) return;

    if (listening) {
      const rec = recognitionRef.current as { stop: () => void } | null;
      rec?.stop();
      setListening(false);
      return;
    }

    const SpeechRecognition =
      (window as unknown as Record<string, unknown>).SpeechRecognition ??
      (window as unknown as Record<string, unknown>).webkitSpeechRecognition;
    const recognition = new (SpeechRecognition as new () => {
      continuous: boolean;
      interimResults: boolean;
      lang: string;
      onresult: (e: { results: { transcript: string; isFinal: boolean }[][] }) => void;
      onend: () => void;
      onerror: () => void;
      start: () => void;
      stop: () => void;
    })();

    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = "en-GH";

    recognition.onresult = (e) => {
      const last = e.results[e.results.length - 1];
      if (last?.[0]) {
        setValue((prev) => {
          // Replace interim with final
          const base = prev.replace(/\[listening\.\.\.\]$/, "").trim();
          const transcript = last[0]!.transcript;
          return base ? `${base} ${transcript}` : transcript;
        });
      }
    };

    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }, [listening]);

  const submit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [value, disabled, onSend]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit],
  );

  const onInput = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${String(Math.min(el.scrollHeight, 120))}px`;
  }, []);

  // Send button state class
  const sendClass = disabled ? "send loading" : hasText ? "send ready" : "send empty";

  return (
    <div className="composer">
      <div className="composer-inner">
        <div className="composer-textarea-wrap">
          {onAttach && (
            <button
              type="button"
              className="attach-btn"
              onClick={onAttach}
              disabled={disabled}
              aria-label="Attach file"
              title="Attach a document"
            >
              <Icon name="plus" size={16} />
            </button>
          )}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            onInput={onInput}
            placeholder="What are you working toward?"
            aria-label="Message SIKA"
            rows={1}
            disabled={disabled}
          />
        </div>
        {SPEECH_AVAILABLE && (
          <button
            type="button"
            className={`voice-btn${listening ? " active" : ""}`}
            onClick={toggleVoice}
            disabled={disabled}
            aria-label={listening ? "Stop listening" : "Voice input"}
            title={listening ? "Stop listening" : "Voice input"}
          >
            <Icon name={listening ? "close" : "mic"} size={14} />
          </button>
        )}
        <button
          type="button"
          className={sendClass}
          onClick={submit}
          disabled={disabled || !hasText}
          aria-label="Send message"
        >
          {disabled ? (
            <span className="send-spinner" />
          ) : (
            <Icon name="arrow" size={16} />
          )}
        </button>
      </div>
      <div className={`composer-hint desktop-only${hasText ? " visible" : ""}`}>
        <span>
          <span className="kbd">Enter</span> to send · <span className="kbd">Shift + Enter</span>{" "}
          for new line
        </span>
      </div>
    </div>
  );
}
